/**
 * Normalize the JSONB `images[]` shapes in `properties` and `rooms`.
 *
 * Rows can hold either plain string URLs (legacy Storyblok mirror) or
 * `{url, alt}` objects (future admin uploads). Use these helpers anywhere
 * you need a flat list of strings or "the first viewable image."
 */

export function imageEntryUrl(entry: unknown): string | null {
  if (typeof entry === "string") return entry.trim() || null;
  if (entry && typeof entry === "object") {
    const o = entry as Record<string, unknown>;
    if (typeof o.url === "string" && o.url.trim()) return o.url.trim();
    if (typeof o.filename === "string" && o.filename.trim())
      return o.filename.trim();
  }
  return null;
}

export function extractImageUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    const url = imageEntryUrl(entry);
    if (url) out.push(url);
  }
  return out;
}

export function resolveCoverImage(
  rowImages: unknown,
  fallbackUrl?: string | null
): string | null {
  const urls = extractImageUrls(rowImages);
  return urls[0] ?? fallbackUrl ?? null;
}

/** The CMS the bare asset paths in `rooms.images` belong to. */
const STORYBLOK_ORIGIN = "https://a.storyblok.com";

/**
 * Repair the double-prefixed Storyblok URLs the CMS sync produced, and turn a
 * bare Storyblok asset path into an absolute one.
 *
 * Three shapes appear in `rooms.images` in production:
 *   "https://a.storyblok.com/f/https://a.storyblok.com/f/…" — a sync bug
 *   "f/123/…"                                              — bare asset path
 *   "abc123/photo.jpg"                                     — bare filename
 * Everything already absolute is returned untouched.
 *
 * The Storyblok prefix is a fallback for RELATIVE paths only, and it is a
 * guess — a well-informed one, because every non-absolute value measured in
 * that column is a Storyblok asset, but a guess all the same. So it is applied
 * as narrowly as the data allows:
 *
 *   · `data:` / `blob:` are already complete references. Prefixing one
 *     produces a URL that is not merely wrong but unrecognisable.
 *   · A ROOT-RELATIVE path ("/img/x.jpg") is a deliberate statement about the
 *     app's own origin. Storyblok assets never start with a slash, so a
 *     leading "/" is positive evidence this is not one — sending it to a CDN
 *     that has never heard of it turns a working image into a 404.
 *   · Anything else relative keeps the historical behaviour, because that is
 *     the shape the CMS actually wrote.
 */
export function normaliseAssetUrl(raw: string | null): string | null {
  const url = (raw ?? "").trim();
  if (!url) return null;

  const doubled = `${STORYBLOK_ORIGIN}/f/${STORYBLOK_ORIGIN}/`;
  if (url.startsWith(doubled)) {
    return `${STORYBLOK_ORIGIN}/` + url.slice(doubled.length);
  }
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // Already a complete reference, or explicitly about this app's own origin.
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/")) return url;

  if (url.startsWith("f/")) return `${STORYBLOK_ORIGIN}/${url}`;
  return `${STORYBLOK_ORIGIN}/f/${url}`;
}

/**
 * The one photo to show for a room: its first image, else the property's.
 *
 * Tolerates `images` arriving as a JSON *string* as well as an array —
 * jsonb normally comes back parsed, but some rows were written as text and a
 * listing that silently loses its photo is worse than a cheap try/catch.
 *
 * NOTE: Properties.tsx, PropertyDetail.tsx and AdminProperties.tsx still carry
 * their own inline copies of this logic. This is the version to converge on;
 * they were left alone here to keep an unrelated change from touching three
 * large pages.
 */
export function resolveRoomPhoto(
  roomImages: unknown,
  propertyImageUrl?: string | null
): string | null {
  let images = roomImages;
  if (typeof images === "string") {
    try {
      images = JSON.parse(images);
    } catch {
      // A non-JSON string is a single URL, not a list.
      return normaliseAssetUrl(images as string);
    }
  }
  const first = extractImageUrls(images)[0] ?? null;
  return (
    normaliseAssetUrl(first) ?? normaliseAssetUrl(propertyImageUrl ?? null)
  );
}
